/**
 * Currency Utilities
 * Provides consistent currency formatting across the application
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  flag: string;
  decimals: number;
  position: 'before' | 'after';
}

// Supported currencies with full metadata
export const CURRENCIES: Record<string, CurrencyInfo> = {
  NOK: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴', decimals: 2, position: 'after' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', decimals: 2, position: 'before' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', decimals: 2, position: 'before' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', decimals: 2, position: 'before' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪', decimals: 2, position: 'after' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone', flag: '🇩🇰', decimals: 2, position: 'after' },
  CHF: { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭', decimals: 2, position: 'before' },
  CAD: { code: 'CAD', symbol: '$', name: 'Canadian Dollar', flag: '🇨🇦', decimals: 2, position: 'before' },
  AUD: { code: 'AUD', symbol: '$', name: 'Australian Dollar', flag: '🇦🇺', decimals: 2, position: 'before' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', decimals: 0, position: 'before' },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳', decimals: 2, position: 'before' },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', decimals: 2, position: 'before' },
  PLN: { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', flag: '🇵🇱', decimals: 2, position: 'after' },
  CZK: { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', flag: '🇨🇿', decimals: 2, position: 'after' },
};

// Default currency
export const DEFAULT_CURRENCY = 'NOK';

/**
 * Get currency info by code
 */
export function getCurrency(code: string): CurrencyInfo {
  return CURRENCIES[code?.toUpperCase()] || CURRENCIES[DEFAULT_CURRENCY];
}

/**
 * Format amount with currency symbol
 * @param amount - The amount to format
 * @param currencyCode - ISO 4217 currency code (e.g., 'NOK', 'USD')
 * @param options - Formatting options
 */
export function formatCurrency(
  amount: number,
  currencyCode: string = DEFAULT_CURRENCY,
  options: {
    showDecimals?: boolean;
    compact?: boolean;
    showCode?: boolean;
  } = {}
): string {
  const currency = getCurrency(currencyCode);
  const { showDecimals = true, compact = false, showCode = false } = options;
  
  // Format the number
  let formattedAmount: string;
  
  if (compact && amount >= 1000) {
    if (amount >= 1000000) {
      formattedAmount = (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      formattedAmount = (amount / 1000).toFixed(1) + 'K';
    } else {
      formattedAmount = amount.toString();
    }
  } else {
    const decimals = showDecimals ? currency.decimals : 0;
    formattedAmount = amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).replace(/,/g, ' '); // Use space as thousand separator for Nordic style
  }
  
  // Add currency symbol
  const symbol = showCode ? currency.code : currency.symbol;
  
  if (currency.position === 'before') {
    return `${symbol}${formattedAmount}`;
  } else {
    return `${formattedAmount} ${symbol}`;
  }
}

/**
 * Format hourly rate with currency
 */
export function formatHourlyRate(rate: number, currencyCode: string = DEFAULT_CURRENCY): string {
  return `${formatCurrency(rate, currencyCode, { showDecimals: false })}/t`;
}

/**
 * Parse currency amount from string
 */
export function parseCurrencyAmount(value: string): number {
  // Remove currency symbols, spaces, and convert to number
  const cleaned = value.replace(/[^\d.,\-]/g, '').replace(/,/g, '.');
  return parseFloat(cleaned) || 0;
}

/**
 * Get currency symbol only
 */
export function getCurrencySymbol(currencyCode: string = DEFAULT_CURRENCY): string {
  return getCurrency(currencyCode).symbol;
}

/**
 * Currency list for dropdowns
 */
export function getCurrencyList(): CurrencyInfo[] {
  return Object.values(CURRENCIES);
}
